import React, { useEffect, useState } from "react";
import "./Portfolio.css";
import { Helmet } from "react-helmet";
import { FaExternalLinkAlt } from "react-icons/fa";
import Header from "../../Components/Header";
import Cursor from "../../Components/Cursor";
import { ServicesSectionProvider } from "../../Context/ServiceSectionContext";
import axios from "axios";
import Skeleton, { SkeletonTheme } from "react-loading-skeleton";
import "react-loading-skeleton/dist/skeleton.css";
import ColorBG from "../../Components/Overlay";

const Portfolio = () => {
    const [isNavigate, setIsNavigate] = useState(true)
    const [selectedCategory, setSelectedCategory] = useState("all");
    const [isMore, setIsMore] = useState(false);
    const [categories, setCategories] = useState([]);
    const [projects, setProjects] = useState([]);
    const [loadingCategories, setLoadingCategories] = useState(true);
    const [loadingProjects, setLoadingProjects] = useState(true);

    const fetchData = async () => {
        try {
            setLoadingProjects(true);
            setLoadingCategories(true);
            const serviceReq = axios.get("https://api.glyphqai.com/api/get-servicetype");
            const portfolioReq = axios.get("https://api.glyphqai.com/api/get-portfolio");
            const [serviceRes, portfolioRes] = await Promise.all([serviceReq, portfolioReq]);
            setCategories(serviceRes.data.service_type);
            setProjects(portfolioRes.data.portfolio);
        } catch (err) {
            console.error("Failed to fetch Services:", err);
            console.error("Failed to fetch Projects:", err);
        } finally {
            setLoadingCategories(false);
            setLoadingProjects(false);
        }
    };

    useEffect(() => {
        window.scrollTo(0, 0);
        fetchData();
        setIsNavigate(!isNavigate);
    }, []);

    const handleCategoryClick = (category) => {
        setSelectedCategory(category);
    };

    const handleCategoryResponsiveness = () => {
        setIsMore(!isMore);
    };

    const filteredProjects =
        selectedCategory === "all"
            ? projects
            : projects.filter((project) => project.service_id === selectedCategory);

    return (
        <>
            <Helmet>
                <title>Portfolio - Showcasing Innovative Projects</title>
                <meta name="description" content="Explore our portfolio to see examples of our innovative projects across various categories including web and app development, and more." />
                <meta name="keywords" content="portfolio, web development, app development, NFT services, digital projects" />
                <meta property="og:title" content="Portfolio - Showcasing Innovative Projects" />
                <meta property="og:description" content="Discover our diverse portfolio showcasing our expertise in web development, app development, and NFT services." />
                <meta property="og:image" content="/path_to_portfolio_image.jpg" />
                <meta property="og:type" content="website" />
                <meta property="og:url" content={window.location.href} />
            </Helmet>

            <ServicesSectionProvider>
                <Cursor />
                <Header />
                <div className="container-fluid pt-5 mt-5">
                    <div className="row">
                        <div className="col-12 col-sm-4">
                            <div className="category-heading">Categories</div>
                        </div>

                        <div className="col-12 col-sm-8">
                            <div className="categories">
                                {loadingCategories ? (
                                    <SkeletonTheme baseColor="#ddd" highlightColor="#f5f5f5">
                                        <Skeleton count={10} width={120} height={50} inline={true} style={{ margin: "10px", borderRadius: "20px" }} />
                                    </SkeletonTheme>
                                ) : (
                                    <>
                                        <span
                                            className={`category ${selectedCategory === "all" ? "active" : ""}`}
                                            onClick={() => handleCategoryClick("all")}
                                        >
                                            all
                                        </span>
                                        {categories.map((cat, index) => (
                                            <span
                                                key={index}
                                                className={`category ${selectedCategory === cat.title ? "active" : ""}`}
                                                onClick={() => handleCategoryClick(cat.id)}
                                            >
                                                {cat.title}
                                            </span>
                                        ))}
                                    </>
                                )}
                            </div>
                            <span className="more-btn d-block d-lg-none" onClick={handleCategoryResponsiveness}>
                                {isMore ? "Less" : "More"}
                            </span>
                        </div>
                    </div>

                    <section>
                        <div className="container-fluid mt-2">
                            <div className="row row-gap-3 Edstudio-equal-height">
                                {loadingProjects
                                    ? Array(6)
                                        .fill()
                                        .map((_, index) => (
                                            <div key={index} className={`col-${index % 4 === 0 || index % 4 === 3 ? "md-4" : "md-8"} col-sm-12`}>
                                                <SkeletonTheme baseColor="#e0e0e0" highlightColor="#f8f8f8">
                                                    <Skeleton height={350} className="rounded shimmer-effect" />
                                                </SkeletonTheme>
                                            </div>
                                        ))
                                    : filteredProjects.map((project, index) => (
                                        <div
                                            key={project.id}
                                            className={`col-${index % 4 === 0 || index % 4 === 3 ? "md-4" : "md-8"} col-sm-12 Edstudio-column h-75`}
                                            data-category={project.service_id}
                                        >
                                            {project.type === "video" ? (
                                                <video
                                                    src={`https://api.glyphqai.com/uploads/portfolio/${project.value}`}
                                                    className="Edstudio-hero-video h-100 rounded"
                                                    autoPlay
                                                    muted
                                                    loop
                                                ></video>
                                            ) : (
                                                <img
                                                    src={`https://api.glyphqai.com/uploads/portfolio/${project.value}`}
                                                    className="Edstudio-hero-video h-100 rounded"
                                                    alt={`project for ${project.title}`}
                                                />
                                            )}
                                            <div className="Edstudio-button-container d-flex flex-column justify-content-between mt-2">
                                                <div className="two-buttons d-flex flex-row">
                                                    <button className="Edstudio-button btn-agda fw-bold">GLYPH</button>
                                                    <a href={project.link} className="Edstudio-button btn-btn-agda-circle rounded-5">
                                                        <FaExternalLinkAlt />
                                                    </a>
                                                </div>
                                                <button className="Edstudio-button-p rounded-5">View Project</button>
                                            </div>
                                        </div>
                                    ))}
                            </div>
                        </div>
                    </section>
                </div>
            </ServicesSectionProvider>
            <ColorBG style={{ top: `${isNavigate ? "0%" : "150%"}`, transform: `${isNavigate ? "rotate(0deg)" : "rotate(10deg)"}`, }} />
        </>
    );
};

export default Portfolio;
